import {
    lazy,
    Suspense,
} from "react";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SidebarProvider } from "./context/SidebarContext";
import "./App.css";

const Layout = lazy(() => import("./components/Layout"));

const Home = lazy(() => import("./pages/Home"));
const Callback = lazy(() => import("./pages/Callback"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Changelog = lazy(() => import("./pages/Changelog"));

const MyProfile = lazy(() => import("./pages/MyProfile"));
const ServerInfo = lazy(() => import("./pages/ServerInfo"));
const MessageWelcomeGb = lazy(
    () => import("./pages/MessageWelcomeGb"),
);
const AutomaticRoles = lazy(
    () => import("./pages/AutomaticRoles"),
);
const RoleReaction = lazy(
    () => import("./pages/RoleReaction"),
);
const InvitationCollaboration = lazy(
    () => import("./pages/InvitationCollaboration"),
);
const MyServerCollaborations = lazy(
    () => import("./pages/MyServerCollaborations"),
);
const TwitchNotification = lazy(
    () => import("./pages/TwitchNotification"),
);
const YoutubeNotification = lazy(
    () => import("./pages/YoutubeNotification"),
);
const LogsStats = lazy(() => import("./pages/LogsStats"));
const MemberActivity = lazy(
    () => import("./pages/MemberActivity"),
);
const ChoosePremium = lazy(
    () => import("./pages/ChoosePremium"),
);
const ModerationSettings = lazy(
    () => import("./pages/ModerationSettings"),
);
const Moderation = lazy(
    () => import("./pages/Moderation"),
);
const MemberProfile = lazy(
    () => import("./pages/MemberProfile"),
);

const RouteLoader = () => (
    <div
        className="nexus-route-loader"
        role="status"
        aria-live="polite"
    >
        <div className="nexus-route-loader-mark">
            <span>N</span>
            <Loader2
                className="nexus-route-loader-spinner"
                size={70}
            />
        </div>

        <strong>Nexus</strong>
        <span>Chargement du tableau de bord…</span>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const token = localStorage.getItem("access_token");

    if (!token) {
        return (
            <Navigate
                to="/home"
                replace
                state={{
                    from: location.pathname,
                }}
            />
        );
    }

    return children;
};

const DashboardShell = () => (
    <ProtectedRoute>
        <SidebarProvider>
            <Layout />
        </SidebarProvider>
    </ProtectedRoute>
);

const AppRoutes = () => (
    <Suspense fallback={<RouteLoader />}>
        <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/callback" element={<Callback />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/changelog" element={<Changelog />} />

            {/* Dashboard authentifié */}
            <Route
                path="/dashboard"
                element={<DashboardShell />}
            >
                <Route
                    index
                    element={
                        <Navigate to="welcome" replace />
                    }
                />

                <Route
                    path="welcome"
                    element={<MyProfile />}
                />

                <Route
                    path="server_info"
                    element={<ServerInfo />}
                />

                <Route
                    path="message_welcome_gb"
                    element={<MessageWelcomeGb />}
                />

                <Route
                    path="roles"
                    element={<AutomaticRoles />}
                />

                <Route
                    path="role_reaction"
                    element={<RoleReaction />}
                />

                <Route
                    path="invitation_collaboration"
                    element={<InvitationCollaboration />}
                />

                <Route
                    path="my_server_collaborations"
                    element={<MyServerCollaborations />}
                />

                <Route
                    path="twitch_notifications"
                    element={<TwitchNotification />}
                />

                <Route
                    path="youtube_notifications"
                    element={<YoutubeNotification />}
                />

                <Route
                    path="logs_stats"
                    element={<LogsStats />}
                />

                <Route
                    path="member_activity"
                    element={<MemberActivity />}
                />

                <Route
                    path="member_profile"
                    element={<MemberProfile />}
                />

                <Route
                    path="moderation_settings"
                    element={<ModerationSettings />}
                />

                <Route
                    path="moderation"
                    element={<Moderation />}
                />

                <Route
                    path="premium"
                    element={<ChoosePremium />}
                />

                {/* URL inconnue à l’intérieur du dashboard */}
                <Route
                    path="*"
                    element={
                        <Navigate to="welcome" replace />
                    }
                />
            </Route>

            {/* URL publique inconnue */}
            <Route
                path="*"
                element={<Navigate to="/home" replace />}
            />
        </Routes>
    </Suspense>
);

const App = () => (
    <BrowserRouter>
        <AppRoutes />
    </BrowserRouter>
);

export default App;